import { TimeRange } from 'features/systemLens'
import type { PointAnnotation } from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import type { Ruler } from 'features/timeSeries/components/rulerTool/useRulerTool'
import type { ActiveSentinel } from 'features/timeSeries/hooks/useSentinelScores'
import {
  loadDataProcessingConfigsFromChart,
  loadDateConfigFromChart,
  loadDerivedValuesFromChart,
  loadPointAnnotationsFromChart,
  loadRulerConfigsFromChart,
  loadSensorsFromChart,
  loadSentinelsFromChart,
  loadSourceConfigProperties,
} from 'features/timeSeries/utils/chartConfigLoaders'
import { Dispatch, SetStateAction, useCallback } from 'react'
import {
  BaseSensorEntry,
  ChartInteractionMode,
  ChartTimeUnit,
  DateMode,
  RegressionConfig,
  SmoothingConfig,
  StandaloneChart,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

interface ApplyChartConfigParams {
  chart: StandaloneChart | null | undefined
  sensorEntries: BaseSensorEntry[]
  derivedValueEntries: TimeSeriesDerivedValueEntryJSON[]
  cadenceSeconds: number
  mode: 'merge' | 'replace'
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  setDerivedValues: Dispatch<SetStateAction<TimeSeriesDerivedValueEntryJSON[]>>
  setCustomSensorColors: Dispatch<SetStateAction<Record<string, string>>>
  setSensorVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  setCustomAxisRanges: Dispatch<
    SetStateAction<Record<string, { min?: number; max?: number }>>
  >
  setAutoScaleMode: Dispatch<SetStateAction<boolean>>
  setSingleScaleMode: Dispatch<SetStateAction<boolean>>
  setDateMode: Dispatch<SetStateAction<DateMode>>
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  setLiveTail: Dispatch<SetStateAction<boolean>>
  setRelativeStart: Dispatch<
    SetStateAction<{ value?: number; unit: ChartTimeUnit }>
  >
  setRelativeEnd: Dispatch<
    SetStateAction<{ isNow: boolean; value?: number; unit: ChartTimeUnit }>
  >
  setCadenceSeconds: Dispatch<SetStateAction<number>>
  /**
   * Optional so existing callers are unaffected. When omitted, a chart's
   * `data_processing_configs` are simply not applied — same as today.
   */
  setThresholdFilters?: Dispatch<
    SetStateAction<Record<string, ThresholdFilterConfig>>
  >
  setSmoothingConfigs?: Dispatch<
    SetStateAction<Record<string, SmoothingConfig>>
  >
  setRegressionConfigs?: Dispatch<SetStateAction<RegressionConfig[]>>
  /** Ruler / annotation / open-tool state. Optional on the same terms as above. */
  setRulers?: Dispatch<SetStateAction<Ruler[]>>
  setPointAnnotations?: Dispatch<SetStateAction<PointAnnotation[]>>
  setInteractionMode?: Dispatch<SetStateAction<ChartInteractionMode>>
  /**
   * Sentinel overlay state. Optional on the same terms as above. Restoring
   * sentinels never re-adds the target pen (it is saved as its own sensor
   * config) and never touches the data-layer toggles — the provider decides
   * whether the Sentinels layer should light up.
   */
  setActiveSentinels?: Dispatch<SetStateAction<ActiveSentinel[]>>
  setSentinelVisibilityConfig?: Dispatch<
    SetStateAction<Record<string, boolean>>
  >
}

/** Union two id-keyed lists, letting the incoming entry win — as regressions do. */
const mergeById = <T extends { id: string }>(prev: T[], incoming: T[]): T[] => {
  const byId = new Map(prev.map((item) => [item.id, item]))
  for (const item of incoming) byId.set(item.id, item)
  return [...byId.values()]
}

export const useApplyChartConfig = () => {
  const applyChartConfig = useCallback(
    ({
      chart,
      sensorEntries,
      derivedValueEntries,
      cadenceSeconds,
      mode,
      setSelectedSensors,
      setDerivedValues,
      setCustomSensorColors,
      setSensorVisibilityConfig,
      setCustomAxisRanges,
      setAutoScaleMode,
      setSingleScaleMode,
      setDateMode,
      setTimeRange,
      setLiveTail,
      setRelativeStart,
      setRelativeEnd,
      setCadenceSeconds,
      setThresholdFilters,
      setSmoothingConfigs,
      setRegressionConfigs,
      setRulers,
      setPointAnnotations,
      setInteractionMode,
      setActiveSentinels,
      setSentinelVisibilityConfig,
    }: ApplyChartConfigParams) => {
      if (!chart) return

      const sensors = loadSensorsFromChart(chart, sensorEntries)
      if (sensors.length > 0 || mode === 'replace') {
        setSelectedSensors(sensors)
      }

      const derivedVals = loadDerivedValuesFromChart(chart, derivedValueEntries)
      if (derivedVals.length > 0 || mode === 'replace') {
        setDerivedValues(derivedVals)
      }

      if (chart.sourceConfigs.length > 0 || mode === 'replace') {
        const { colors, visibility, axisRanges, hasCustomRanges } =
          loadSourceConfigProperties(chart)

        if (mode === 'merge') {
          setCustomSensorColors((prev) => ({ ...prev, ...colors }))
          setSensorVisibilityConfig((prev) => ({ ...prev, ...visibility }))
          setCustomAxisRanges((prev) => ({ ...prev, ...axisRanges }))
        } else {
          setCustomSensorColors(colors)
          setSensorVisibilityConfig(visibility)
          setCustomAxisRanges(axisRanges)
        }

        if (hasCustomRanges) {
          setAutoScaleMode(false)
        } else if (mode === 'replace') {
          setAutoScaleMode(true)
        }
      }

      if (chart.yAxisMode) {
        setSingleScaleMode(chart.yAxisMode === 'single')
      }

      const { thresholdFilters, smoothingConfigs, regressionConfigs } =
        loadDataProcessingConfigsFromChart(chart)

      // Mirrors the source-config branch above: in 'merge' the chart's configs
      // layer over live state, in 'replace' (revert) they become the state
      // outright — including clearing to empty for a chart that saved none.
      if (mode === 'merge') {
        setThresholdFilters?.((prev) => ({ ...prev, ...thresholdFilters }))
        setSmoothingConfigs?.((prev) => ({ ...prev, ...smoothingConfigs }))
        setRegressionConfigs?.((prev) => {
          const byId = new Map(prev.map((config) => [config.id, config]))
          for (const config of regressionConfigs) byId.set(config.id, config)
          return [...byId.values()]
        })
      } else {
        setThresholdFilters?.(thresholdFilters)
        setSmoothingConfigs?.(smoothingConfigs)
        setRegressionConfigs?.(regressionConfigs)
      }

      // Same merge/replace split as the configs above: in 'merge' the chart's
      // rulers and annotations layer over live state by id, in 'replace'
      // (revert) they become the state outright — including clearing to empty
      // for a chart that saved none.
      const restoredRulers = loadRulerConfigsFromChart(chart)
      const restoredAnnotations = loadPointAnnotationsFromChart(chart)

      if (mode === 'merge') {
        setRulers?.((prev) => mergeById(prev, restoredRulers))
        setPointAnnotations?.((prev) => mergeById(prev, restoredAnnotations))
      } else {
        setRulers?.(restoredRulers)
        setPointAnnotations?.(restoredAnnotations)
      }

      // Same split again for sentinel overlays. Keyed by entry id (their
      // `mergeById` id field is `entryId`, hence the inline merge). In
      // 'replace' (revert) a chart that saved none clears live sentinels.
      const { activeSentinels, sentinelVisibility } =
        loadSentinelsFromChart(chart)

      if (mode === 'merge') {
        setActiveSentinels?.((prev) => {
          const byEntryId = new Map(
            prev.map((sentinel) => [sentinel.entryId, sentinel])
          )
          for (const sentinel of activeSentinels) {
            byEntryId.set(sentinel.entryId, sentinel)
          }
          return [...byEntryId.values()]
        })
        setSentinelVisibilityConfig?.((prev) => ({
          ...prev,
          ...sentinelVisibility,
        }))
      } else {
        setActiveSentinels?.(activeSentinels)
        setSentinelVisibilityConfig?.(sentinelVisibility)
      }

      // Nullish for charts saved before `interaction_mode` existed; those open
      // with no tool active, which is the pre-existing behaviour.
      setInteractionMode?.(chart.interactionMode ?? 'none')

      const dateConfig = loadDateConfigFromChart(chart, cadenceSeconds)
      if (dateConfig) {
        setDateMode(dateConfig.dateMode)
        setTimeRange(dateConfig.timeRange)
        setLiveTail(dateConfig.liveTail)
        setRelativeStart(dateConfig.relativeStart)
        setRelativeEnd(dateConfig.relativeEnd)
        setCadenceSeconds(dateConfig.cadenceSeconds)
      }
    },
    []
  )

  return { applyChartConfig }
}
